import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaHome,
  FaPlus,
  FaSave,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import MdlAssignTimetableCell from "../dialog/MdlAssignTimetableCell";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const WEEKDAYS = [
  { id: 1, short: "Mon", label: "Monday" },
  { id: 2, short: "Tue", label: "Tuesday" },
  { id: 3, short: "Wed", label: "Wednesday" },
  { id: 4, short: "Thu", label: "Thursday" },
  { id: 5, short: "Fri", label: "Friday" },
  { id: 6, short: "Sat", label: "Saturday" },
];

const PERIOD_TYPES = [
  { value: "teaching", label: "Teaching" },
  { value: "break", label: "Break" },
  { value: "lunch", label: "Lunch" },
  { value: "prayer", label: "Prayer" },
  { value: "assembly", label: "Assembly" },
  { value: "other", label: "Other" },
];

//Created By Cursor
function flattenClassSections(classData = []) {
  const out = [];
  classData.forEach((item) => {
    if (!item) return;
    if (item.section === "No") {
      out.push({
        key: `${item.class_id}-No`,
        class_id: item.class_id,
        class_name: item.class_name,
        section_id: "No",
        section_name: "",
        board_id: item.board_id,
        medium_id: item.medium_id,
      });
      return;
    }
    const sections = Array.isArray(item.section) ? item.section : [];
    sections.forEach((sec) => {
      out.push({
        key: `${sec.class_id || item.class_id}-${sec.section_id}`,
        class_id: sec.class_id || item.class_id,
        class_name: sec.class_name || item.class_name,
        section_id: sec.section_id,
        section_name: sec.section_name,
        board_id: item.board_id,
        medium_id: item.medium_id,
      });
    });
  });
  return out;
}

//Created By Cursor
function classLabel(row) {
  if (!row) return "—";
  if (!row.section_name || row.section_id === "No") return row.class_name || "—";
  return `${row.class_name}:${row.section_name}`;
}

//Created By Cursor
function emptyPeriod(index = 0) {
  const startH = 9 + Math.floor(index * 0.7);
  const startM = index % 2 === 0 ? "00" : "40";
  const start = `${String(startH).padStart(2, "0")}:${startM}`;
  return {
    local_id: `p_${Date.now()}_${index}`,
    period_no: index + 1,
    period_name: `Period ${index + 1}`,
    period_type: "teaching",
    start_time: `${start}:00`.slice(0, 8),
    end_time: `${String(startH).padStart(2, "0")}:${startM === "00" ? "40" : "20"}:00`,
    duration_minutes: 40,
  };
}

//Created By Cursor
function entryKey(periodId, weekday) {
  return `${periodId}_${weekday}`;
}

//Created By Cursor
export default function TimetableBuilder() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;
  const params = useParams();
  const routeScheduleId = params.id || params.schedule_id || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [stage, setStage] = useState(1); // 1 list/create · 2 periods · 3 grid
  const [newName, setNewName] = useState("Main Timetable");
  const [classOptions, setClassOptions] = useState([]);
  const [classKey, setClassKey] = useState("");
  const [entriesMap, setEntriesMap] = useState({});
  const [assignOptions, setAssignOptions] = useState({
    subjects: [],
    staff: [],
    pairs: [],
  });
  const [cellEdit, setCellEdit] = useState(null); // { period, weekday, subject_id, staff_id }
  const [cellAssignOptions, setCellAssignOptions] = useState(null);
  const [loadingCellOptions, setLoadingCellOptions] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const selectedClass = useMemo(
    () => classOptions.find((c) => c.key === classKey) || null,
    [classOptions, classKey]
  );

  //Created By Cursor
  const loadSchedules = useCallback(async (sid) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    const res = await axios.get(`${API_BASE()}getTimetableSchedules.php`, {
      params: { school_code: schoolCode, session_id: sid || undefined },
    });
    if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
      setSchedules(res.data.data);
    } else {
      setSchedules([]);
    }
  }, []);

  //Created By Cursor
  const loadClasses = useCallback(async (sid) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sid) return;
    const res = await axios.get(`${API_BASE()}getClass.php`, {
      params: { school_code: schoolCode, session_id: sid },
    });
    if (res.data?.api_status === "OK") {
      setClassOptions(flattenClassSections(res.data.data?.class || []));
    }
  }, []);

  //Created By Cursor
  const openSchedule = useCallback(
    async (id) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !id) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE()}getTimetable.php`, {
          params: { school_code: schoolCode, id },
        });
        if (res.data?.api_status === "OK" && res.data.data) {
          const data = res.data.data;
          setSchedule(data);
          setPeriods(
            (data.periods || []).map((p, i) => ({
              ...p,
              local_id: `db_${p.id || i}`,
              start_time: String(p.start_time || "").slice(0, 8),
              end_time: String(p.end_time || "").slice(0, 8),
            }))
          );
          const map = {};
          (data.entries || []).forEach((e) => {
            map[entryKey(e.period_id, e.weekday)] = e;
          });
          setEntriesMap(map);
          setStage(2);
          history.replace(`/AdminMain/TimetableBuilder/${data.id}`);
        } else {
          myToast.notify_warning(res.data?.api_remark || "Timetable not found.");
        }
      } catch (err) {
        console.error("[TimetableBuilder] open:", err);
        myToast.notify_warning("Failed to load timetable.");
      } finally {
        setLoading(false);
      }
    },
    [history]
  );

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    const sid = current?.session_id || "";
    setSessionId(sid);
    (async () => {
      setLoading(true);
      try {
        await loadSchedules(sid);
        await loadClasses(sid);
        if (routeScheduleId) {
          await openSchedule(routeScheduleId);
        } else {
          setStage(1);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [routeScheduleId, loadSchedules, loadClasses, openSchedule]);

  //Created By Cursor
  const createSchedule = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails() || {};
    if (!schoolCode || !sessionId) {
      myToast.notify_warning("School or session missing.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE()}saveTimetableSchedule.php`, {
        school_code: schoolCode,
        session_id: sessionId,
        name: newName.trim() || "Main Timetable",
        created_by: staff.staff_id || "",
        action: "create",
      });
      if (res.data?.api_status === "OK" && res.data.data) {
        myToast.notify_success("Timetable created.");
        setSchedule(res.data.data);
        setPeriods(
          (res.data.data.periods || []).map((p, i) => ({
            ...p,
            local_id: `db_${p.id || i}`,
          }))
        );
        setEntriesMap({});
        setStage(2);
        history.replace(`/AdminMain/TimetableBuilder/${res.data.data.id}`);
        await loadSchedules(sessionId);
      } else {
        myToast.notify_warning(res.data?.api_remark || "Create failed.");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Failed to create timetable.");
    } finally {
      setSaving(false);
    }
  }, [sessionId, newName, history, loadSchedules]);

  //Created By Cursor
  const savePeriods = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !schedule?.id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE()}saveTimetablePeriods.php`, {
        school_code: schoolCode,
        schedule_id: schedule.id,
        periods: periods.map((p, i) => ({
          period_no: i + 1,
          period_name: p.period_name,
          period_type: p.period_type,
          start_time: p.start_time,
          end_time: p.end_time,
          duration_minutes: Number(p.duration_minutes) || 0,
        })),
      });
      if (res.data?.api_status === "OK" && res.data.data) {
        myToast.notify_success("Periods saved.");
        setSchedule(res.data.data);
        setPeriods(
          (res.data.data.periods || []).map((p, i) => ({
            ...p,
            local_id: `db_${p.id || i}`,
          }))
        );
        setEntriesMap({});
        setStage(3);
      } else {
        myToast.notify_warning(res.data?.api_remark || "Save periods failed.");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Failed to save periods.");
    } finally {
      setSaving(false);
    }
  }, [schedule, periods]);

  //Created By Cursor
  const loadClassEntries = useCallback(
    async (cls) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !schedule?.id || !cls) return;
      setLoadingOptions(true);
      try {
        const [ttRes, optRes] = await Promise.all([
          axios.get(`${API_BASE()}getTimetable.php`, {
            params: {
              school_code: schoolCode,
              id: schedule.id,
              class_id: cls.class_id,
              section_id: cls.section_id,
            },
          }),
          axios.get(`${API_BASE()}getTimetableAssignOptions.php`, {
            params: {
              school_code: schoolCode,
              session_id: schedule.session_id || sessionId,
              class_id: cls.class_id,
              section_id: cls.section_id,
            },
          }),
        ]);
        const map = {};
        if (ttRes.data?.api_status === "OK") {
          (ttRes.data.data?.entries || []).forEach((e) => {
            map[entryKey(e.period_id, e.weekday)] = e;
          });
          if (ttRes.data.data?.periods?.length) {
            setPeriods(
              ttRes.data.data.periods.map((p, i) => ({
                ...p,
                local_id: `db_${p.id || i}`,
              }))
            );
          }
        }
        setEntriesMap(map);
        if (optRes.data?.api_status === "OK") {
          setAssignOptions(optRes.data.data || { subjects: [], staff: [], pairs: [] });
        } else {
          setAssignOptions({ subjects: [], staff: [], pairs: [] });
        }
      } catch (err) {
        console.error(err);
        myToast.notify_warning("Failed to load class timetable.");
      } finally {
        setLoadingOptions(false);
      }
    },
    [schedule, sessionId]
  );

  useEffect(() => {
    if (stage === 3 && selectedClass) {
      loadClassEntries(selectedClass);
    }
  }, [stage, selectedClass, loadClassEntries]);

  //Created By Cursor
  const openCell = useCallback(
    async (period, weekday) => {
      if (period.period_type && period.period_type !== "teaching") {
        myToast.notify_warning("Only teaching periods can be assigned.");
        return;
      }
      if (!selectedClass) {
        myToast.notify_warning("Select a class first.");
        return;
      }
      const existing = entriesMap[entryKey(period.id, weekday)] || {};
      setCellEdit({
        period,
        weekday,
        subject_id: existing.subject_id || "",
        staff_id: existing.staff_id || "",
      });
      setCellAssignOptions(null);
      const schoolCode = SessionManager.getSchoolCode();
      const periodId = Number(period.id) || 0;
      if (!schoolCode || !schedule?.id || !periodId) {
        myToast.notify_warning("Period is not ready. Save periods first.");
        return;
      }
      setLoadingCellOptions(true);
      try {
        const optRes = await axios.get(`${API_BASE()}getTimetableAssignOptions.php`, {
          params: {
            school_code: schoolCode,
            session_id: schedule.session_id || sessionId,
            class_id: selectedClass.class_id,
            section_id: selectedClass.section_id || "No",
            schedule_id: schedule.id,
            period_id: periodId,
            weekday,
          },
        });
        if (optRes.data?.api_status === "OK") {
          setCellAssignOptions(
            optRes.data.data || { subjects: [], staff: [], pairs: [] }
          );
        } else {
          // Fall back to class-level options (without busy flags) so dialog still works
          setCellAssignOptions(
            assignOptions || { subjects: [], staff: [], pairs: [] }
          );
          myToast.notify_warning(
            optRes.data?.api_remark || "Could not check staff availability."
          );
        }
      } catch (err) {
        console.error(err);
        setCellAssignOptions(
          assignOptions || { subjects: [], staff: [], pairs: [] }
        );
        myToast.notify_warning("Could not check staff availability.");
      } finally {
        setLoadingCellOptions(false);
      }
    },
    [entriesMap, selectedClass, schedule, sessionId, assignOptions]
  );

  //Created By Cursor
  const saveCell = useCallback(
    async (override = null) => {
      if (!cellEdit || !selectedClass || !schedule?.id) return;
      const schoolCode = SessionManager.getSchoolCode();
      const subject_id =
        override && Object.prototype.hasOwnProperty.call(override, "subject_id")
          ? override.subject_id
          : cellEdit.subject_id;
      const staff_id =
        override && Object.prototype.hasOwnProperty.call(override, "staff_id")
          ? override.staff_id
          : cellEdit.staff_id;
      setSaving(true);
      try {
        const res = await axios.post(`${API_BASE()}saveTimetableEntry.php`, {
          school_code: schoolCode,
          schedule_id: schedule.id,
          period_id: Number(cellEdit.period.id) || cellEdit.period.id,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id || "No",
          weekday: cellEdit.weekday,
          subject_id: subject_id || "",
          staff_id: staff_id || "",
        });
        if (res.data?.api_status === "OK") {
          myToast.notify_success(res.data.message || "Saved.");
          setCellEdit(null);
          setCellAssignOptions(null);
          await loadClassEntries(selectedClass);
        } else {
          const c = res.data?.staff_clash;
          if (c) {
            const clashLabel = [
              c.class_name || c.class_id,
              c.section_name && c.section_name !== "No" ? c.section_name : "",
            ]
              .filter(Boolean)
              .join(" — ");
            myToast.notify_warning(
              res.data?.api_remark ||
                `Staff already assigned in ${clashLabel || "another class"}`
            );
          } else {
            myToast.notify_warning(res.data?.api_remark || "Save failed.");
          }
        }
      } catch (err) {
        console.error(err);
        myToast.notify_warning("Failed to save cell.");
      } finally {
        setSaving(false);
      }
    },
    [cellEdit, selectedClass, schedule, loadClassEntries]
  );

  //Created By Cursor
  const deleteSchedule = useCallback(
    async (id) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!window.confirm("Delete this timetable and all assignments?")) return;
      setSaving(true);
      try {
        const res = await axios.post(`${API_BASE()}saveTimetableSchedule.php`, {
          school_code: schoolCode,
          id,
          action: "delete",
        });
        if (res.data?.api_status === "OK") {
          myToast.notify_success("Deleted.");
          if (String(schedule?.id) === String(id)) {
            setSchedule(null);
            setPeriods([]);
            setEntriesMap({});
            setStage(1);
            history.replace("/AdminMain/TimetableBuilder");
          }
          await loadSchedules(sessionId);
        } else {
          myToast.notify_warning(res.data?.api_remark || "Delete failed.");
        }
      } catch (err) {
        console.error(err);
        myToast.notify_warning("Failed to delete.");
      } finally {
        setSaving(false);
      }
    },
    [schedule, sessionId, history, loadSchedules]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  const teachingPeriods = periods.filter(
    (p) => !p.period_type || p.period_type === "teaching"
  );

  return (
    <div
      className="content payroll-page sm-page tt-page"
      style={{ background: "#f5f7fb", fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="tt-crumb"
                onClick={() => history.push("/AdminMain/Academic/1")}
              >
                Academic
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Timetable Builder</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCalendarAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Timetable Builder</h1>
              <p className="sm-hero__sub">
                Staff & subjects from staff subject mapping · session{" "}
                {sessionId || "—"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {stage > 1 ? (
              <button
                type="button"
                className="tt-btn tt-btn--ghost"
                onClick={() => {
                  if (stage === 3) setStage(2);
                  else {
                    setSchedule(null);
                    setStage(1);
                    history.replace("/AdminMain/TimetableBuilder");
                  }
                }}
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            ) : null}
            <button
              type="button"
              className={`tt-icon-btn${loading ? " is-spinning" : ""}`}
              title="Refresh"
              disabled={loading || saving}
              onClick={() => loadSchedules(sessionId)}
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="tt-body">
          <nav className="tt-stepper" aria-label="Timetable stages">
            <span className={`tt-step${stage === 1 ? " is-active" : ""}`}>1 · Plans</span>
            <span className={`tt-step${stage === 2 ? " is-active" : ""}`}>2 · Periods</span>
            <span className={`tt-step${stage === 3 ? " is-active" : ""}`}>3 · Assign</span>
          </nav>

          {loading ? (
            <div className="tt-loading">
              <MyLoader />
              <p>Loading…</p>
            </div>
          ) : null}

          {!loading && stage === 1 ? (
            <section className="tt-panel">
              <div className="tt-create">
                <label className="tt-field tt-field--grow">
                  <span>New timetable name</span>
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Main Timetable"
                  />
                </label>
                <button
                  type="button"
                  className="tt-btn tt-btn--primary"
                  disabled={saving}
                  onClick={createSchedule}
                >
                  <FaPlus />
                  <span>{saving ? "Creating…" : "Create"}</span>
                </button>
              </div>

              <h3 className="tt-section-title">Existing plans</h3>
              {schedules.length === 0 ? (
                <p className="tt-empty">No timetables yet for this session.</p>
              ) : (
                <div className="tt-schedule-list">
                  {schedules.map((s) => (
                    <article key={s.id} className="tt-schedule-row">
                      <div className="min-w-0">
                        <strong>{s.name}</strong>
                        <p>
                          {s.period_count || 0} periods · {s.entry_count || 0}{" "}
                          assignments · {s.status}
                        </p>
                      </div>
                      <div className="tt-schedule-row__actions">
                        <button
                          type="button"
                          className="tt-btn tt-btn--accent"
                          onClick={() => openSchedule(s.id)}
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          className="tt-icon-btn tt-icon-btn--danger"
                          title="Delete"
                          onClick={() => deleteSchedule(s.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ) : null}

          {!loading && stage === 2 && schedule ? (
            <section className="tt-panel">
              <div className="tt-panel__head">
                <div>
                  <h3>{schedule.name}</h3>
                  <p className="tt-hint">
                    Define bell periods. Saving periods clears existing class
                    assignments.
                  </p>
                </div>
                <div className="tt-panel__actions">
                  <button
                    type="button"
                    className="tt-btn tt-btn--ghost"
                    onClick={() =>
                      setPeriods((prev) => [...prev, emptyPeriod(prev.length)])
                    }
                  >
                    <FaPlus /> Add period
                  </button>
                  <button
                    type="button"
                    className="tt-btn tt-btn--primary"
                    disabled={saving || !periods.length}
                    onClick={savePeriods}
                  >
                    <FaSave />
                    <span>{saving ? "Saving…" : "Save & assign"}</span>
                  </button>
                </div>
              </div>

              <div className="tt-period-list">
                {periods.map((p, index) => (
                  <div key={p.local_id || p.id || index} className="tt-period-row">
                    <span className="tt-period-row__no">{index + 1}</span>
                    <label className="tt-field">
                      <span>Name</span>
                      <input
                        value={p.period_name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPeriods((prev) =>
                            prev.map((x, i) =>
                              i === index ? { ...x, period_name: v } : x
                            )
                          );
                        }}
                      />
                    </label>
                    <label className="tt-field">
                      <span>Type</span>
                      <select
                        value={p.period_type || "teaching"}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPeriods((prev) =>
                            prev.map((x, i) =>
                              i === index ? { ...x, period_type: v } : x
                            )
                          );
                        }}
                      >
                        {PERIOD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="tt-field tt-field--time">
                      <span>Start</span>
                      <input
                        type="time"
                        value={String(p.start_time || "").slice(0, 5)}
                        onChange={(e) => {
                          const v = `${e.target.value}:00`;
                          setPeriods((prev) =>
                            prev.map((x, i) =>
                              i === index ? { ...x, start_time: v } : x
                            )
                          );
                        }}
                      />
                    </label>
                    <label className="tt-field tt-field--time">
                      <span>End</span>
                      <input
                        type="time"
                        value={String(p.end_time || "").slice(0, 5)}
                        onChange={(e) => {
                          const v = `${e.target.value}:00`;
                          setPeriods((prev) =>
                            prev.map((x, i) =>
                              i === index ? { ...x, end_time: v } : x
                            )
                          );
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="tt-icon-btn tt-icon-btn--danger"
                      title="Remove"
                      onClick={() =>
                        setPeriods((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              {periods.some((p) => p.id) ? (
                <button
                  type="button"
                  className="tt-btn tt-btn--accent"
                  style={{ marginTop: 12 }}
                  onClick={() => setStage(3)}
                >
                  Continue to assign
                </button>
              ) : null}
            </section>
          ) : null}

          {!loading && stage === 3 && schedule ? (
            <section className="tt-panel">
              <div className="tt-panel__head">
                <div>
                  <h3>Assign · {schedule.name}</h3>
                </div>
                <label className="tt-field tt-field--class">
                  <span>Class / section</span>
                  <select
                    value={classKey}
                    onChange={(e) => setClassKey(e.target.value)}
                  >
                    <option value="">Select class</option>
                    {classOptions.map((c) => (
                      <option key={c.key} value={c.key}>
                        {classLabel(c)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {!selectedClass ? (
                <p className="tt-empty">Select a class to edit its weekly grid.</p>
              ) : loadingOptions ? (
                <div className="tt-loading">
                  <MyLoader />
                </div>
              ) : (
                <div className="tt-grid-wrap">
                  <table className="tt-grid">
                    <thead>
                      <tr>
                        <th>
                          <FaClock aria-hidden /> Period
                        </th>
                        {WEEKDAYS.map((d) => (
                          <th key={d.id}>{d.short}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {periods.map((p) => {
                        const isTeaching =
                          !p.period_type || p.period_type === "teaching";
                        return (
                          <tr key={p.id || p.local_id}>
                            <th>
                              <div className="tt-grid__period">
                                <strong>{p.period_name}</strong>
                                <span>
                                  {String(p.start_time || "").slice(0, 5)}–
                                  {String(p.end_time || "").slice(0, 5)}
                                </span>
                                {!isTeaching ? (
                                  <em className="tt-grid__type">{p.period_type}</em>
                                ) : null}
                              </div>
                            </th>
                            {WEEKDAYS.map((d) => {
                              if (!isTeaching) {
                                return (
                                  <td key={d.id} className="tt-grid__break">
                                    —
                                  </td>
                                );
                              }
                              const cell = entriesMap[entryKey(p.id, d.id)];
                              return (
                                <td key={d.id}>
                                  <button
                                    type="button"
                                    className={`tt-cell${cell ? " has-value" : ""}`}
                                    onClick={() => openCell(p, d.id)}
                                  >
                                    {cell ? (
                                      <>
                                        <strong>
                                          {cell.subject_name || cell.subject_id}
                                        </strong>
                                        <span>
                                          {cell.staff_name || cell.staff_id || "—"}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="tt-cell__empty">Assign</span>
                                    )}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!teachingPeriods.length ? (
                    <p className="tt-empty">No teaching periods defined.</p>
                  ) : null}
                  {(assignOptions.pairs || []).length === 0 ? (
                    <p className="tt-hint" style={{ marginTop: 12 }}>
                      No staff–subject mapping found for this class in{" "}
                      <code>tm_staff_subject</code>. Add teaching classes there
                      to populate teachers.
                    </p>
                  ) : null}
                </div>
              )}
            </section>
          ) : null}
        </div>
      </div>

      {cellEdit ? (
        <MdlAssignTimetableCell
          cell={cellEdit}
          classLabel={selectedClass ? classLabel(selectedClass) : ""}
          assignOptions={
            cellAssignOptions || { subjects: [], staff: [], pairs: [] }
          }
          loadingOptions={loadingCellOptions || !cellAssignOptions}
          saving={saving}
          onClose={() => {
            setCellEdit(null);
            setCellAssignOptions(null);
          }}
          onSave={saveCell}
        />
      ) : null}
    </div>
  );
}
